import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';

import { format } from 'date-fns';

import { AgePicker } from 'us-react-components/dist/AgePicker/AgePicker';
import 'us-react-components/node_modules/react-datepicker/dist/react-datepicker.css';
import { InfoCard } from 'us-react-components/dist/InfoCard/InfoCard';
import { SavedSearch, SavedSearchService } from 'us-web-services';
import toast from 'react-hot-toast';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { Counter } from 'us-react-components/dist/Counter/Counter';
import { useDependencies } from '../../../dependencies';
import { FlowType } from '../../../models/FamilyRegistrationStore/constants';
import { Routes } from '../../../Routes';

const MIN_COUNT = 1;
const MAX_COUNT = 9;

const today = new Date();
const NINE_MONTHS_FROM_TODAY = new Date(today.setMonth(today.getMonth() + 8));

const todayTwo = new Date();
const EIGHTEEN_YEARS_AGO = new Date(todayTwo.setFullYear(todayTwo.getFullYear() - 18));

export const Count = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    services: { tracker },
    stores: { familyRegistration }
  } = useDependencies();

  familyRegistration.setCurrentRoute(Routes.parent.registration.count);
  useEffect(() => {
    if (familyRegistration.flowType === FlowType.ChildCare && !familyRegistration.childrenAges.length) {
      familyRegistration.resetDependents();
    }
  }, []);

  const increment = (): void => {
    const trackingProps = {
      elementId: 'counter-plus',
      elementName: 'counter-plus',
      elementText: '+',
    };

    tracker.track('clicked-button', trackingProps);

    if (familyRegistration.countDependents < MAX_COUNT) {
      familyRegistration.setCountDependents(familyRegistration.countDependents + 1);
      if (familyRegistration.flowType === FlowType.ChildCare) {
        familyRegistration.setChildrenAges([...familyRegistration.childrenAges, { id: familyRegistration.childrenAges.length, value: '' }]);
      }
    }
  };

  const decrement = (): void => {
    const trackingProps = {
      elementId: 'counter-minus',
      elementName: 'counter-minus',
      elementText: '-',
    };

    tracker.track('clicked-button', trackingProps);

    if (familyRegistration.countDependents > MIN_COUNT) {
      if (familyRegistration.flowType === FlowType.ChildCare) {
        const ages = [...familyRegistration.childrenAges];
        ages.pop();
        familyRegistration.setChildrenAges(ages);
      }
      familyRegistration.setCountDependents(familyRegistration.countDependents - 1);
    } else {
      familyRegistration.resetDependents();
    }
  };

  const updateSearch = async (): Promise<void> => {
    try {
      const savedSearch: SavedSearch = {
        id: familyRegistration.savedSearchId ?? undefined,
        numberOfChildren: familyRegistration.countDependents,
      };

      if (familyRegistration.savedSearchId) {
        await SavedSearchService.updateSearch(familyRegistration.savedSearchId, savedSearch);
      }
    } catch {
      toast.error(t('pages.nux.family.count.errorUpdatingSearch'));
    }
  };

  const handleContinue = (): void => {
    updateSearch();
    navigate(Routes.parent.registration.profilePreview)
  }

  const getTitle = (): string => {
    if (familyRegistration.flowType === FlowType.PetCareRecurring || familyRegistration.flowType === FlowType.PetCareBrowsing) {
      return t('pages.nux.family.count.petsTitle');
    }

    if (familyRegistration.flowType === FlowType.SeniorCare) {
      return t('pages.nux.family.count.adultsTitle');
    }

    return t('pages.nux.family.count.childrenTitle');
  }

  const getCounterText = (): string => {
    if (familyRegistration.flowType === FlowType.PetCareRecurring || familyRegistration.flowType === FlowType.PetCareBrowsing) {
      return t('pages.nux.family.count.pets');
    }

    if (familyRegistration.flowType === FlowType.SeniorCare) {
      return t('pages.nux.family.count.adults');
    }

    return t('pages.nux.family.count.children');
  }

  const getCounterSubText = (): string => {
    if (familyRegistration.flowType === FlowType.ChildCare) {
      return t('pages.nux.family.count.childrenSubText');
    }

    return '';
  }

  const renderInfoCard = (): JSX.Element | undefined => familyRegistration.flowType === FlowType.ChildCare ?
    <InfoCard
      iconName={faHeart}
      title={t('pages.nux.family.count.infoCard.title')}
      description={t('pages.nux.family.count.infoCard.description')}
    /> : undefined;

  const handleDateChange = (id: number, value: string): void => {
    const updatedInputs = familyRegistration.childrenAges.map((input) =>
      input.id === id ? { ...input, value } : input
    );
    familyRegistration.setChildrenAges(updatedInputs);
  };

  const renderDateInputs = (): JSX.Element => (
    <div className='justify-center items-center flex flex-wrap'>
      {familyRegistration.childrenAges.map((input) => (
        <div key={input.id}>
          <p className='w-[130px] mt-2 ml-2 text-xs text-almostBlack-300'>{t('pages.nux.family.count.childAge', { count: input.id + 1 })}</p>
          <AgePicker
            key={input.id}
            maxDate={NINE_MONTHS_FROM_TODAY}
            minDate={EIGHTEEN_YEARS_AGO}
            onChange={(date: Date): void => {
              handleDateChange(input.id, format(date, 'MM/yyyy'));
            }}
            value={input.value}
          />
        </div>
      ))}
    </div>
  )

  const isContinueDisabled = (): boolean => {
    if (familyRegistration.flowType === FlowType.ChildCare) {
      return familyRegistration.childrenAges.some((input) => input.value === '');
    }
    return false;
  }

  return (
    <NuxContainer
      infoCard={renderInfoCard()}
      familyRegistration={familyRegistration}
      navigate={navigate}
      tracker={tracker}
      title={getTitle()}
      onContinue={handleContinue}
      isContinueDisabled={isContinueDisabled()}
      continueTitle={t('pages.nux.family.count.button')}
    >
      <div className='px-6 xs:px-8 pt-2 flex flex-col items-center'>
        <div className='flex items-center'>
          <div>
            <span className='text-xl'>{getCounterText()} </span>
            <span className='text-x2 text-almostBlack-300'>{getCounterSubText()}</span>
          </div>
          <Counter count={familyRegistration.countDependents} increment={increment} decrement={decrement} />
        </div>
        <div className='pt-4 ml-3'>
          {familyRegistration.flowType === FlowType.ChildCare && renderDateInputs()}
        </div>
      </div>
    </NuxContainer>
  )
});
